/**
 * OptionField — renders a single config-driven input field.
 *
 * Extracted and generalised from FilterField (formsUtils.jsx).
 * Uses generic naming: `fieldConfig` (not `filterStructure`), `Key` (not `FilterName`).
 *
 * Supported Types: text, toggleInput, multiselect, sizeInput, dateInput
 */
import {Controller, useWatch} from "react-hook-form";
import {useCallback, useEffect, useMemo, useState} from "react";
import {FormHelperText, Grid, TextField} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import clsx from "clsx";
import {ToggleButtonGroupField, useStyles} from "../../utils/uiUtils";
import {isNumberLike, isPlainDict, toBytes} from "../../utils/utils";

/**
 * Returns default value for a field based on its Type.
 */
export const getFieldDefault = (fieldConfig) => {
    const type = fieldConfig.Type;
    const options = fieldConfig.Options || {};

    switch (type) {
        case "toggleInput":
            return {defaultValue: options.defaultValue ?? false};
        case "dateInput":
            return {defaultValue: ''};
        case "multiselect":
            return {defaultValue: []};
        case "sizeInput":
            return {defaultValue: "", sizeUnitValue: 'B'};
        default:
            return {defaultValue: ""};
    }
};

/**
 * OptionField component — renders one field from a config descriptor.
 *
 * @param {Object} fieldConfig — { Key, Label, Type, Options, CategoryName, SubCategoryName }
 * @param {Function} getOtherFieldFn — (key) => fieldConfig | null — for cross-field dependencies
 * @param {Object} control — react-hook-form control
 * @param {Function} trigger — react-hook-form trigger
 * @param {Object} errors — react-hook-form errors
 * @param {Function} getValuesFn — react-hook-form getValues
 * @param {Object} supportData — resolved options for multiselect fields
 * @param {boolean} disabled — externally controlled disabled state
 */
export const OptionField = ({
                                fieldConfig,
                                getOtherFieldFn,
                                control,
                                trigger = undefined,
                                errors,
                                getValuesFn,
                                supportData = {},
                                disabled = false,
                            }) => {

    const fieldKey = fieldConfig.Key;

    const {defaultValue, defaultSizeUnitValue = '', fieldProps, rules} = useMemo(() => {
        const cfg = {
            toggleInput: {
                defaultValue: getFieldDefault(fieldConfig).defaultValue,
            },
            text: {
                defaultValue: getFieldDefault(fieldConfig).defaultValue,
                fieldProps: {
                    type: 'text',
                    inputProps: {maxLength: 300, inputMode: 'text'},
                },
                rules: {
                    maxLength: {value: 300, message: 'Maximum 300 characters'},
                },
            },
            int: {
                defaultValue: getFieldDefault(fieldConfig).defaultValue,
                fieldProps: {
                    type: 'number',
                    inputProps: {step: 1, pattern: '[0-9]*', inputMode: 'numeric'},
                },
                rules: {
                    valueAsNumber: true,
                    pattern: {value: /^\d+$/, message: 'Whole numbers only'},
                },
            },
            sizeInput: {
                defaultValue: getFieldDefault(fieldConfig).defaultValue,
                defaultSizeUnitValue: getFieldDefault(fieldConfig).sizeUnitValue,
                fieldProps: {
                    type: 'number',
                    inputProps: {step: 0.01, min: 0, max: 99999999.99, inputMode: 'decimal'},
                },
                rules: {
                    valueAsNumber: true,
                    pattern: {
                        value: /^\d+([.,]\d+)?$/,
                        message: 'Decimal number (e.g. 123.45)',
                    },
                },
            },
            dateInput: {
                defaultValue: getFieldDefault(fieldConfig).defaultValue,
                fieldProps: {
                    type: 'date',
                    InputLabelProps: {shrink: true},
                },
                rules: {},
            },
            multiselect: {
                defaultValue: getFieldDefault(fieldConfig).defaultValue,
                fieldProps: {type: undefined},
            },
        };
        return cfg[fieldConfig.Type] ?? {defaultValue: ""};
    }, [fieldConfig]);

    const classes = useStyles();

    // ─── Cross-field dependency (existing types: in_larger_category, upper_limit, lower_limit) ──
    const [dependsOnKey, setDependsOnKey] = useState(undefined);
    const [dependencyType, setDependencyType] = useState(undefined);
    const dependentFieldValue = useWatch({
        control,
        name: dependsOnKey || "__noop__",
        defaultValue: undefined,
        disabled: !dependsOnKey,
    });

    useEffect(() => {
        const dep = fieldConfig.Options?.EntryValidation?.Dependency;
        setDependsOnKey(dep || undefined);
        setDependencyType(fieldConfig.Options?.EntryValidation?.DependencyType || undefined);
    }, [fieldConfig]);

    const errorColorFix = {
        FormHelperTextProps: {sx: {fontWeight: 500}},
        sx: {
            "& .MuiFormHelperText-root.Mui-error": {color: "#FFC540", fontWeight: 500},
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {borderColor: "#FFC540"},
            "& .MuiFormLabel-root.Mui-error": {color: "#FFC540"},
        },
    };

    const renderHint = () => {
        if (!fieldConfig.Hint) return null;
        return (
            <Typography sx={{
                color: disabled ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.5)',
                fontSize: '11px',
                fontStyle: 'italic',
                fontFamily: 'Poppins, sans-serif',
                mt: 0.5,
            }}>
                * {fieldConfig.Hint}
            </Typography>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDERERS
    // ═══════════════════════════════════════════════════════════════════════════

    const renderTextField = () => {
        if (fieldConfig.Type !== 'text' && fieldConfig.Type !== 'textarea') return null;

        const isMultiline = fieldConfig.Type === 'textarea';
        const rows = fieldConfig.Options?.rows || 4;

        return (
            <Grid item xs={12}>
                <Controller
                    name={fieldKey}
                    control={control}
                    defaultValue={defaultValue}
                    rules={rules}
                    render={({field, fieldState}) => (
                        <TextField
                            {...field}
                            {...fieldProps}
                            {...errorColorFix}
                            className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                            label={fieldConfig.Label || 'Unknown Label'}
                            error={!!fieldState.error}
                            fullWidth
                            helperText={fieldState.error?.message}
                            disabled={disabled}
                            multiline={isMultiline}
                            minRows={isMultiline ? rows : undefined}
                        />
                    )}
                />
                {renderHint()}
            </Grid>
        );
    };

    const renderToggleField = () => {
        if (fieldConfig.Type !== 'toggleInput') return null;

        return (
            <Grid item xs={12}>
                <Controller
                    name={fieldKey}
                    control={control}
                    defaultValue={defaultValue}
                    render={({field, fieldState}) => (
                        <FormControl
                            error={!!fieldState.error}
                            component="fieldset"
                            variant="standard"
                            className={classes.switchLabelWhite}
                            disabled={disabled}
                        >
                            <FormControlLabel
                                label={fieldConfig.Label || 'Unknown Label'}
                                disabled={disabled}
                                control={
                                    <Switch
                                        inputRef={field.ref}
                                        checked={!!field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        onBlur={field.onBlur}
                                        disabled={disabled}
                                    />
                                }
                            />
                            {fieldState.error && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
                        </FormControl>
                    )}
                />
                {renderHint()}
            </Grid>
        );
    };

    const renderSelectField = () => {
        const isMulti = fieldConfig.Type === 'multiselect';
        if (fieldConfig.Type !== 'multiselect' && fieldConfig.Type !== 'select') return null;

        const optionsSource = fieldConfig.Options?.EntrySource;
        let menuItemList = [];

        if (typeof optionsSource === 'string') menuItemList = [...(supportData?.[optionsSource] ?? [])];
        else if (Array.isArray(optionsSource)) menuItemList = [...optionsSource];

        // Cross-field filtering (in_larger_category)
        let filteredMenuItemList = [];

        if (!dependentFieldValue || (Array.isArray(dependentFieldValue) && dependentFieldValue.length === 0)) {
            filteredMenuItemList = [...menuItemList];
        } else {
            const dependentFieldConfig = getOtherFieldFn(dependsOnKey);

            if (!dependentFieldConfig) {
                filteredMenuItemList = [...menuItemList];
            } else if (!Array.isArray(dependentFieldValue)) {
                filteredMenuItemList = [...menuItemList];
            } else {
                if (dependencyType !== 'in_larger_category') {
                    filteredMenuItemList = [...menuItemList];
                } else {
                    const validationSet = new Set();
                    const depEntryValue = dependentFieldConfig.Options?.EntryValue;
                    const depSource = dependentFieldConfig.Options?.EntrySource;

                    let depAllEntries = [];
                    if (typeof depSource === 'string') depAllEntries = [...(supportData?.[depSource] || [])];
                    else if (Array.isArray(depSource)) depAllEntries = [...depSource];

                    depAllEntries.forEach(entry => {
                        if (dependentFieldValue.includes(entry[depEntryValue])) {
                            const propToExtract = fieldConfig.Options?.EntryValidation?.DependencyProperty;
                            validationSet.add(entry[propToExtract]);
                        }
                    });

                    const localProp = fieldConfig.Options?.EntryValidation?.LocalProperty;
                    filteredMenuItemList = menuItemList.filter(option => {
                        const refs = option[localProp];
                        if (!Array.isArray(refs)) return validationSet.has(refs);
                        return refs.some(val => validationSet.has(val));
                    });
                }
            }
        }

        const displayKey = fieldConfig.Options?.EntryDisplay;
        filteredMenuItemList.sort((a, b) =>
            String(a[displayKey] ?? "").localeCompare(String(b[displayKey] ?? ""))
        );

        const formControlErrorColorFix = {
            "& .MuiFormHelperText-root.Mui-error": {color: "#FFC540", fontWeight: 500},
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {borderColor: "#FFC540"},
            "& .MuiFormLabel-root.Mui-error": {color: "#FFC540"},
        };

        return (
            <Grid item xs={12}>
                <Controller
                    name={fieldKey}
                    control={control}
                    defaultValue={isMulti ? [] : ""}
                    rules={{...rules, onChange: () => trigger(fieldKey)}}
                    render={({field, fieldState}) => {
                        const value = isMulti
                            ? (Array.isArray(field.value) ? field.value : [])
                            : (field.value ?? "");

                        const handleChange = (event) => {
                            const {value} = event.target;
                            field.onChange(isMulti ? (Array.isArray(value) ? value : [value]) : value);
                            trigger?.(fieldKey);
                        };

                        return (
                            <FormControl
                                fullWidth
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                error={!!fieldState.error}
                                sx={formControlErrorColorFix}
                                disabled={disabled}
                            >
                                <InputLabel id={`${field.name}-label`}>
                                    {fieldConfig.Label || "Unknown Label"}
                                </InputLabel>
                                <Select
                                    {...field}
                                    labelId={`${field.name}-label`}
                                    multiple={isMulti}
                                    value={value}
                                    onChange={handleChange}
                                    disabled={disabled}
                                    input={<OutlinedInput label={fieldConfig.Label || "Unknown Label"} {...fieldProps} />}
                                >
                                    {filteredMenuItemList.map((option, idx) => (
                                        <MenuItem
                                            key={`${field.name}_item_${idx}`}
                                            value={option[fieldConfig.Options?.EntryValue]}
                                        >
                                            {option[fieldConfig.Options?.EntryDisplay]}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {fieldState.error?.message && (
                                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                )}
                            </FormControl>
                        );
                    }}
                />
                {renderHint()}
            </Grid>
        );
    };

    const renderSizeInputField = () => {
        if (fieldConfig.Type !== 'sizeInput') return null;

        if (dependencyType && !['upper_limit', 'lower_limit'].includes(dependencyType)) return null;

        const validateSize = (val) => {
            if (val === '' || val === undefined || val === null) return true;
            if (isNaN(val)) return 'Size must be a number';
            if (!dependencyType) return true;

            const values = getValuesFn();
            const curUnit = values[`${fieldKey}_size_unit`];
            const depVal = values[dependsOnKey];
            const depUnit = values[`${dependsOnKey}_size_unit`];

            if (depVal === '' || depVal === undefined || depVal === null) return true;
            if (isPlainDict(depVal)) return true;
            if (!isNumberLike(depVal)) return 'Dependent field value must be a number';

            const curBytes = toBytes(val, curUnit);
            const depBytes = toBytes(depVal, depUnit);

            if (dependencyType === 'upper_limit' && curBytes > depBytes) {
                return `Size cannot be greater than ${depVal} ${depUnit}.`;
            }
            if (dependencyType === 'lower_limit' && curBytes < depBytes) {
                return `Size cannot be less than ${depVal} ${depUnit}.`;
            }
            return true;
        };

        const enrichedRules = dependencyType ? {...rules, validate: validateSize} : rules;
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];

        return (
            <>
                <Grid item xs={9}>
                    <Controller
                        name={fieldKey}
                        control={control}
                        defaultValue={defaultValue}
                        rules={enrichedRules}
                        render={({field, fieldState}) => (
                            <TextField
                                {...field}
                                {...fieldProps}
                                {...errorColorFix}
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                label={fieldConfig.Label || 'Unknown Label'}
                                error={!!fieldState.error}
                                fullWidth
                                helperText={fieldState.error?.message}
                                disabled={disabled}
                                onChange={(e) => {
                                    field.onChange(e);
                                    trigger([fieldKey, dependsOnKey]);
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Controller
                        name={`${fieldKey}_size_unit`}
                        control={control}
                        defaultValue={defaultSizeUnitValue}
                        render={({field}) => (
                            <TextField
                                {...field}
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                fullWidth
                                select
                                disabled={disabled}
                                onChange={(e) => {
                                    field.onChange(e);
                                    trigger([fieldKey, dependsOnKey]);
                                }}
                            >
                                {units.map((sizeUnit, index) => (
                                    <MenuItem key={`${fieldKey}_size_unit_${index}`} value={sizeUnit}>
                                        {sizeUnit}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                </Grid>
                {renderHint() && <Grid item xs={12}>{renderHint()}</Grid>}
            </>
        );
    };

    const renderDateInputField = () => {
        if (fieldConfig.Type !== 'dateInput') return null;

        if (dependencyType && !['upper_limit', 'lower_limit'].includes(dependencyType)) return null;

        const validateDate = (val) => {
            if (!val || val === '') return true;
            if (!dependencyType) return true;

            const values = getValuesFn();
            const depVal = values[dependsOnKey];
            if (!depVal || depVal === '') return true;
            if (isPlainDict(depVal)) return true;

            const curDate = new Date(val);
            const depDate = new Date(depVal);

            if (dependencyType === 'upper_limit' && curDate > depDate) {
                return `Date cannot be later than ${depVal}.`;
            }
            if (dependencyType === 'lower_limit' && curDate < depDate) {
                return `Date cannot be earlier than ${depVal}.`;
            }
            return true;
        };

        const enrichedRules = dependencyType ? {...rules, validate: validateDate} : rules;

        return (
            <Grid item xs={12}>
                <Controller
                    name={fieldKey}
                    control={control}
                    defaultValue={defaultValue}
                    rules={enrichedRules}
                    render={({field, fieldState}) => (
                        <TextField
                            {...field}
                            {...fieldProps}
                            {...errorColorFix}
                            className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                            label={fieldConfig.Label || 'Unknown Label'}
                            error={!!fieldState.error}
                            fullWidth
                            helperText={fieldState.error?.message}
                            disabled={disabled}
                            onChange={(e) => {
                                field.onChange(e);
                                trigger([fieldKey, dependsOnKey]);
                            }}
                        />
                    )}
                />
                {renderHint()}
            </Grid>
        );
    };

    const renderToggleButtonGroupField = () => {
        if (fieldConfig.Type !== 'toggleButtonGroup') return null;

        const optionsSource = fieldConfig.Options?.EntrySource;
        let items = [];

        if (typeof optionsSource === 'string') items = [...(supportData?.[optionsSource] ?? [])];
        else if (Array.isArray(optionsSource)) items = [...optionsSource];

        const displayKey = fieldConfig.Options?.EntryDisplay || "label";
        const valueKey = fieldConfig.Options?.EntryValue || "value";

        return (
            <Grid item xs={12} display="flex" flexDirection="column" alignItems="flex-start">
                <Controller
                    name={fieldKey}
                    control={control}
                    defaultValue={defaultValue}
                    render={({field}) => (
                        <ToggleButtonGroupField
                            label={fieldConfig.Label}
                            items={items.map(item => ({
                                label: item[displayKey],
                                value: item[valueKey],
                                disabled: item.disabled === true,
                            }))}
                            value={field.value}
                            onChange={field.onChange}
                            disabled={disabled}
                        />
                    )}
                />
                {renderHint()}
            </Grid>
        );
    };

    // ─── Dispatch ─────────────────────────────────────────────────────────────
    const renderers = {
        text: renderTextField,
        textarea: renderTextField,
        toggleInput: renderToggleField,
        toggleButtonGroup: renderToggleButtonGroupField,
        select: renderSelectField,
        multiselect: renderSelectField,
        sizeInput: renderSizeInputField,
        dateInput: renderDateInputField,
    };

    const renderer = renderers[fieldConfig.Type];
    if (!renderer) {
        console.warn(`OptionField: unknown Type "${fieldConfig.Type}" for Key "${fieldKey}"`);
        return null;
    }
    return renderer();
};
